// The Microsoft sign-in lands on /auth/callback with the tokens (or an error) in the URL fragment.

export const SSO_ERRORS = ['staff_only', 'staff_tenant_only', 'sso_failed', 'cancelled', 'account_disabled'] as const;
export type SsoError = (typeof SSO_ERRORS)[number];

export type AuthFragment =
  | { type: 'tokens'; access_token: string; token_type: string; refresh_token: string | null; return_to: string }
  | { type: 'error'; error: SsoError }
  | { type: 'none' };

/** Only paths inside this app are honoured as `return_to`; anything else lands on the dashboard: "/x", never "//host"
 *  nor "/\host" (browsers read a backslash as a slash), and no tab or newline, which browsers drop from a URL
 *  ("/<tab>/host" is "//host"). React Router's GHSA-wrjc-x8rr-h8h6 is that open redirect. */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value || !/^\/(?![/\\])[^\\\t\n\r]*$/.test(value)) return '/';
  if (value.startsWith('/login') || value.startsWith('/auth/')) return '/';
  return value;
}

export function parseAuthFragment(hash: string): AuthFragment {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return { type: 'none' };
  const params = new URLSearchParams(raw);
  const error = params.get('error');
  if (error) return { type: 'error', error: (SSO_ERRORS as readonly string[]).includes(error) ? (error as SsoError) : 'sso_failed' };
  const access = params.get('access_token');
  if (!access) return { type: 'none' };
  return { type: 'tokens', access_token: access, token_type: params.get('token_type') || 'bearer', refresh_token: params.get('refresh_token'), return_to: safeReturnTo(params.get('return_to')) };
}
